import React, { useEffect } from 'react';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  GrandOpeningContainer,
  HomeServicesGuides,
  HomeServicesContent,
  HomeServicesFAQ,
  HomeServicesPromoPod
} from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { HomeServicesMetadata } from '@thd-olt-component-react/metadata';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { HomeServiceCategory } from '@thd-olt-component-react/service-category';
import {
  StoreHero,
  StoreHeroDetails,
  StoreHeroHeader,
  StoreHeroServices
} from '@thd-olt-component-react/store-hero';

import { NearbyStores } from '../../shared-components/NearbyStores';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';

export const HomeServices = () => {
  declareContexts([QueryContext], { freeze: true });
  const {
    storeNumber, storeZip, state, name, city
  } = useParams();

  const path = useLocation();

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.home-services-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  const phoneNumbersToInclude = {
    services: true,
    store: false,
    pro: false,
    tool: false
  };

  return (
    <>
      <ErrorBoundary name="home-services-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>

      <ErrorBoundary name="home-services-metadata">
        <HomeServicesMetadata storeId={storeNumber} />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="home-services-breadcrumbs">
            <StoreFinderBreadcrumbs
              includeDirectory
              pageName="Home Services"
            />
          </ErrorBoundary>

          <ErrorBoundary name="home-services-grand-opening-pod">
            <GrandOpeningContainer
              contentId={storeNumber}
              pageName="homeServices"
            />
          </ErrorBoundary>

          <ErrorBoundary name="home-services-header">
            <StoreHero
              storeId={storeNumber}
            >
              <StoreHeroHeader
                hideRating
                label="Home Services at "
              />
              <section className="sui-grid sui-grid-cols-12 sui-gap-4">
                <div className="sui-col-span-12 lg:sui-col-span-8">
                  <StoreHeroServices
                    type="home"
                    storeZip={storeZip}
                  />
                </div>
                <div className="sui-col-span-12 lg:sui-col-span-4">
                  <StoreHeroDetails
                    phoneNumbers={phoneNumbersToInclude}
                    showStoreDetailsButton
                    title="Home Services"
                    contentId={storeNumber}
                    pageName="homeServices"
                  />
                </div>
              </section>
            </StoreHero>
          </ErrorBoundary>

          <section className="sui-flex sui-flex-col sui-gap-y-8 sui-mt-8">
            <ErrorBoundary name="home-services-promo-pod">
              <HomeServicesPromoPod contentId={storeNumber} pageName="homeServices" />
            </ErrorBoundary>

            <ErrorBoundary name="home-services-listings">
              <HomeServiceCategory
                title="Home Services at The Home Depot Near You"
                storeZip={storeZip}
              />
            </ErrorBoundary>

            <ErrorBoundary name="emt-home-services-content">
              <HomeServicesContent storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="emt-home-services-faq">
              <HomeServicesFAQ storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="emt-home-services-guides">
              <HomeServicesGuides storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="home-services-nearby-stores">
              <Hydrator
                id="home-services-nearby-stores-map"
                scrollBuffer={800}
              >
                <NearbyStores
                  omitStoreNumber={storeNumber}
                  pageType="services"
                  storeNumber={storeNumber}
                />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="home-services-popular-categories">
              <PopularCategories
                contentId={storeNumber}
                pageType="homeServices"
              />
            </ErrorBoundary>

            <ErrorBoundary name="home-services-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
};

HomeServices.displayName = 'HomeServices';
HomeServices.propTypes = {};

HomeServices.defaultProps = {
  name: '',
};

HomeServices.dataModel = extend(
  Breadcrumbs,
  EmtLinks,
  GrandOpeningContainer,
  HomeServiceCategory,
  HomeServicesContent,
  HomeServicesFAQ,
  HomeServicesGuides,
  HomeServicesMetadata,
  HomeServicesPromoPod,
  NearbyStores,
  PopularCategories,
  StoreFinderRedirector,
  StoreHero,
);
